import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { refreshAccountAccess, validateToken } from "./store/slices/authSlice";
import { tokenManager } from "./utils/tokenManager";
import { canAccessAdministration } from "./utils/appRole";
import { ToastProvider, useToast } from "./contexts/ToastContext";
import { FooterProvider } from "./contexts/FooterContext";
import { AlertDataProvider } from "./contexts/AlertDataContext";
import ToastContainer from "./components/ToastContainer";
import { AccessDenied } from "./components/ProtectedRoute";
import LoginPage from "./pages/LoginPage";
import DashboardLayout from "./components/DashboardLayout";
import Users from "./pages/UserManagement/Users";
import { Toaster } from "react-hot-toast";
import Check from "@mui/icons-material/Check";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import MainAlertServices from "./pages/AlertServices/mainAlertServices";
import { AIDetectionTabsThreeOnly } from "./pages/AlertDetection/AIDetectionTabsThreeOnly.tsx";
import { NvrManagementPage } from "./pages/Nvr/NvrManagementPage.tsx";
import PlaceholderModulePage from "./pages/PlaceholderModulePage";
import FlashbackDashboard from "./pages/FlashBack/Dashboard.tsx";
import CameraPlaybackPage from "./pages/FlashBack/CameraPlaybackPage.tsx";
import Wahankhede360Page from "./pages/Place360/Wahankhede360Page.tsx";
import DigitalPatrolShell from "./pages/ConstructionSiteManagement/patrol/DigitalPatrolShell.jsx";
import AdministrationLayout from "./pages/administration/AdministrationLayout";
import {
  AdministrationStoragePlaceholder,
  AdministrationPatrollingRouteMgmtDemo,
  AdministrationIotDevicesMgmtDemo,
} from "./pages/administration/AdministrationSections";
import { AdministrationAiModelsManagement } from "./pages/administration/AdministrationAiModelsSection.tsx";
import { AdministrationCameraManagement } from "./pages/administration/AdministrationCameraManagement.tsx";
import { AdministrationZoneManagement } from "./pages/administration/AdministrationZoneManagement.tsx";
import { UserRoleAdminPanel } from "./features/userRoleAdmin/UserRoleAdminPanel.tsx";
import PatrolSettingsAdminPage from "./pages/administration/patrolAdmin/PatrolSettingsAdminPage.jsx";
import { LIVE_PATH, LEGACY_LIVE_PATH } from "./config/liveName";

/** Administration routes — only when logged-in user has admin role. */
function AdminRoute({ children }) {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const accountAccess = useSelector((state) => state.auth.accountAccess);
  const accessLoaded = useSelector((state) => state.auth.accessLoaded);
  const loading = useSelector((state) => state.auth.loading);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (loading || !accessLoaded) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        Loading…
      </div>
    );
  }
  if (!canAccessAdministration(accountAccess)) {
    return <Navigate to="/live-monitoring" replace />;
  }
  return children;
}

/** Pill toast with solid circular badge + icon (matches product toast reference). */
function ToastSuccessIcon() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 22,
        height: 22,
        minWidth: 22,
        borderRadius: "50%",
        backgroundColor: "#2E7D32",
        flexShrink: 0,
      }}
      aria-hidden
    >
      <Check style={{ fontSize: 15, color: "#fff" }} />
    </span>
  );
}

function ToastErrorIcon() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 22,
        height: 22,
        minWidth: 22,
        borderRadius: "20%",
        backgroundColor: "#C62828",
        flexShrink: 0,
        color: "#fff",
        fontSize: 13,
        fontWeight: 700,
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        lineHeight: 1,
      }}
      aria-hidden
    >
      !
    </span>
  );
}

function ToastWarningIcon() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 22,
        height: 22,
        minWidth: 22,
        borderRadius: "50%",
        backgroundColor: "#E65100",
        flexShrink: 0,
      }}
      aria-hidden
    >
      <WarningIcon style={{ fontSize: 15, color: "#fff" }} />
    </span>
  );
}

function ToastInfoIcon() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 22,
        height: 22,
        minWidth: 22,
        borderRadius: "50%",
        backgroundColor: "#1565C0",
        flexShrink: 0,
      }}
      aria-hidden
    >
      <InfoIcon style={{ fontSize: 15, color: "#fff" }} />
    </span>
  );
}

// Suppress ResizeObserver loop errors globally (they're harmless but noisy)
const originalError = console.error;
console.error = (...args) => {
  if (
    args[0]?.includes?.(
      "ResizeObserver loop completed with undelivered notifications",
    )
  ) {
    return; // Suppress this specific error
  }
  originalError.apply(console, args);
};

// Suppress ResizeObserver errors in React error overlay
window.addEventListener("error", (event) => {
  if (
    event.message?.includes(
      "ResizeObserver loop completed with undelivered notifications",
    )
  ) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
});

// Suppress ResizeObserver errors in unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
  if (
    event.reason?.message?.includes(
      "ResizeObserver loop completed with undelivered notifications",
    )
  ) {
    event.preventDefault();
    return false;
  }
});

/**
 * App Content Component
 * Contains the main app content with toast functionality
 */
function AppContent() {
  const dispatch = useDispatch();
  const { isAuthenticated, loading } = useSelector((state) => state.auth);
  const { toasts, removeToast } = useToast();

  // Restore session + always refresh permissions after reload
  useEffect(() => {
    if (!tokenManager.isAuthenticated()) return;

    if (!isAuthenticated) {
      dispatch(validateToken());
      return;
    }

    dispatch(refreshAccountAccess());
  }, [dispatch, isAuthenticated]);

  // Show loading spinner while validating token
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster
        position="top-right"
        gutter={12}
        toastOptions={{
          duration: 4000,
          style: {
            background: "#f5f5f5",
            color: "#212121",
            borderRadius: "9999px",
            padding: "10px 18px 10px 12px",
            fontSize: "14px",
            fontWeight: 500,
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            letterSpacing: "0.01em",
            boxShadow:
              "0 4px 18px rgba(0,0,0,0.1), 0 2px 6px rgba(0,0,0,0.06)",
            maxWidth: "min(calc(100vw - 32px), 440px)",
          },

          success: {
            style: {
              background: "#E8F5E9",
              color: "#1B5E20",
            },
            icon: <ToastSuccessIcon />,
          },

          error: {
            style: {
              background: "#FFEBEE",
              color: "#B71C1C",
            },
            icon: <ToastErrorIcon />,
          },

          warning: {
            style: {
              background: "#FFF3E0",
              color: "#E65100",
            },
            icon: <ToastWarningIcon />,
          },

          info: {
            style: {
              background: "#E3F2FD",
              color: "#0D47A1",
            },
            icon: <ToastInfoIcon />,
          },
        }}
      />

      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/live-monitoring" /> : <LoginPage />
          }
        />

        {/* User Management - Users */}
        <Route
          path="/user-management/users"
          element={
            isAuthenticated ? (
              <DashboardLayout>
                <Users />
              </DashboardLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        {/* Construction Site Management */}
        {/* <Route
          path="/construction-site-management"
          element={
            isAuthenticated ? (
              <DashboardLayout>
                <DashboardTabs />
              </DashboardLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        /> */}
        <Route
          path="/ai-analysis"
          element={
            isAuthenticated ? (
              <AlertDataProvider>
                <DashboardLayout>
                  <MainAlertServices />
                </DashboardLayout>
              </AlertDataProvider>
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        {/* Legacy URL → AI Analysis (formerly Alert Services) */}
        <Route
          path="/alert-services"
          element={
            isAuthenticated ? (
              <Navigate to="/ai-analysis" replace />
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        {/* Legacy URL → Live Monitoring */}
        <Route
          path="/ai-detection"
          element={
            isAuthenticated ? (
              <Navigate to="/live-monitoring" replace />
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        {/* Live Monitoring (use cases, zones, configuration) */}
        <Route
          path="/live-monitoring"
          element={
            isAuthenticated ? (
              <DashboardLayout>
                <AIDetectionTabsThreeOnly />
              </DashboardLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        <Route
          path={LIVE_PATH}
          element={
            isAuthenticated ? (
              <DashboardLayout>
                <Wahankhede360Page />
              </DashboardLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        {LIVE_PATH !== LEGACY_LIVE_PATH ? (
          <Route
            path={LEGACY_LIVE_PATH}
            element={<Navigate to={LIVE_PATH} replace />}
          />
        ) : null}
        <Route
          path="/digital-patrol"
          element={
            isAuthenticated ? (
              <DashboardLayout>
                <DigitalPatrolShell />
              </DashboardLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/nvr"
          element={
            isAuthenticated ? (
              <DashboardLayout>
                <NvrManagementPage />
              </DashboardLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        {/* <Route
          path="/ai-analysis"
          element={
            isAuthenticated ? (
              <DashboardLayout>
                <PlaceholderModulePage title="AI Analysis" />
              </DashboardLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        /> */}
        <Route
          path="/flashback"
          element={
            isAuthenticated ? (
              <DashboardLayout>
                <FlashbackDashboard />
              </DashboardLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/cameras/:id/playback"
          element={
            isAuthenticated ? (
              <DashboardLayout>
                <CameraPlaybackPage />
              </DashboardLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        {/* <Route
          path="/smart-reports"
          element={
            isAuthenticated ? (
              <DashboardLayout>
                <PlaceholderModulePage title="Smart Reports" />
              </DashboardLayout>
            ) : (
              <Navigate to="/login" />
            )
          }
        /> */}
        <Route
          path="/administration"
          element={
            <AdminRoute>
              <DashboardLayout>
                <AdministrationLayout />
              </DashboardLayout>
            </AdminRoute>
          }
        >
          <Route index element={<Navigate to="camera-mgmt" replace />} />
          <Route
            path="camera-mgmt"
            element={<AdministrationCameraManagement />}
          />
          <Route
            path="zone-mgmt"
            element={<AdministrationZoneManagement />}
          />
          <Route
            path="ai-models-mgmt"
            element={<AdministrationAiModelsManagement />}
          />
          <Route
            path="alert-rules"
            element={
              <Navigate
                to="/administration/ai-models-mgmt?tab=alert-rules"
                replace
              />
            }
          />
          <Route
            path="alert-services-mgmt"
            element={
              <Navigate
                to="/administration/ai-models-mgmt?tab=alert-rules"
                replace
              />
            }
          />
          <Route
            path="storage-mgmt"
            element={<AdministrationStoragePlaceholder />}
          />
          <Route path="user-mgmt" element={<UserRoleAdminPanel />} />
          <Route
            path="iot-devices-mgmt"
            element={<AdministrationIotDevicesMgmtDemo />}
          />
          <Route
            path="patrolling-route-mgmt"
            element={<AdministrationPatrollingRouteMgmtDemo />}
          />
          <Route path="patrol-settings" element={<PatrolSettingsAdminPage />} />
        </Route>

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/live-monitoring" />} />

        {/* Catch-all route for unauthorized access */}
        <Route
          path="*"
          element={
            <DashboardLayout>
              <AccessDenied message="The page you're looking for doesn't exist or you don't have permission to access it." />
            </DashboardLayout>
          }
        />
      </Routes>
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
}

/**
 * Main application component that handles routing and authentication
 */
function App() {
  return (
    <ToastProvider>
      <FooterProvider>
        <AppContent />
      </FooterProvider>
    </ToastProvider>
  );
}

export default App;
